// src/services/gameLimits.ts
// Service for checking and tracking game limits based on membership tier

const API_URL = process.env.REACT_APP_API_URL || '';

export interface GameLimitStatus {
  success: boolean;
  canPlay: boolean;
  currentCount: number;
  limit: number;
  remaining: number;
  membershipLevel: string;
  message: string;
}

export interface GameLimitsInfo {
  success: boolean;
  membership: {
    level: string;
    points: number;
  };
  limits: {
    aiGamesLimit: number;
    multiplayerGamesLimit: number;
  };
  usage: {
    aiGamesThisMonth: number;
    multiplayerGamesThisMonth: number;
  };
  remaining: {
    aiGamesRemaining: number;
    multiplayerGamesRemaining: number;
  };
  canPlay: {
    aiGame: boolean;
    multiplayerGame: boolean;
  };
}

/**
 * Check if a user can play a specific type of game
 */
export async function canPlayGame(
  userId: string,
  gameType: 'ai' | 'multiplayer'
): Promise<GameLimitStatus> {
  try {
    const response = await fetch(`${API_URL}/api/users/${userId}/can-play/${gameType}`);
    const data = await response.json();

    if (!response.ok) {
      // On server error, allow the game to proceed (graceful degradation)
      console.warn('Game limit check failed, allowing game:', data.message);
      return {
        success: false,
        canPlay: true,
        currentCount: 0,
        limit: 100,
        remaining: 100,
        membershipLevel: 'Unknown',
        message: 'Game limit check unavailable'
      };
    }

    return {
      success: true,
      canPlay: data.canPlay,
      currentCount: data.currentCount,
      limit: data.limit,
      remaining: data.remaining,
      membershipLevel: data.membershipLevel,
      message: data.message
    };
  } catch (error) {
    console.error('Error checking game limit:', error);
    // On error, allow the game to proceed to not block users
    return {
      success: false,
      canPlay: true,
      currentCount: 0,
      limit: 100,
      remaining: 100,
      membershipLevel: 'Unknown',
      message: 'Could not verify game limit'
    };
  }
}

/**
 * Increment the game count for a user
 * Call this when a game actually starts (not when selecting game mode)
 */
export async function incrementGameCount(
  userId: string,
  gameType: 'ai' | 'multiplayer'
): Promise<{ success: boolean; newCount: number; limit: number; remaining: number; message: string }> {
  try {
    const response = await fetch(`${API_URL}/api/users/${userId}/increment-game`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ gameType })
    });

    const data = await response.json();

    if (!response.ok) {
      return {
        success: false,
        newCount: data.currentCount || 0,
        limit: data.limit || 0,
        remaining: 0,
        message: data.message || 'Failed to increment game count'
      };
    }

    return {
      success: true,
      newCount: data.newCount,
      limit: data.limit,
      remaining: data.remaining,
      message: data.message
    };
  } catch (error) {
    console.error('Error incrementing game count:', error);
    return {
      success: false,
      newCount: 0,
      limit: 0,
      remaining: 0,
      message: 'Could not update game count'
    };
  }
}

/**
 * Get full game limits info for a user
 */
export async function getGameLimits(userId: string): Promise<GameLimitsInfo | null> {
  try {
    const response = await fetch(`${API_URL}/api/users/${userId}/game-limits`);

    if (!response.ok) {
      return null;
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching game limits:', error);
    return null;
  }
}

/**
 * Check if user can start an AI game, and increment count if allowed
 * Returns { allowed: true } if game can start, or { allowed: false, message: string } if blocked
 */
export async function startAIGame(userId: string): Promise<{ allowed: boolean; message: string; remaining?: number }> {
  const limitStatus = await canPlayGame(userId, 'ai');

  if (!limitStatus.canPlay) {
    return {
      allowed: false,
      message: limitStatus.message
    };
  }

  // Increment the count
  const incrementResult = await incrementGameCount(userId, 'ai');

  return {
    allowed: true,
    message: `AI game started. ${incrementResult.remaining} games remaining this month.`,
    remaining: incrementResult.remaining
  };
}
